import React, { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import {
  Plus,
  Search,
  Filter,
  Download,
  MoreVertical,
  Eye,
  Edit,
  Send,
  Trash2,
  FileText,
  CheckCircle2,
  Clock,
  AlertCircle,
  X,
  MessageCircle
} from 'lucide-react'
import { useApp } from '../context/AppContext'

const statusFilters = [
  { value: 'all', label: 'All Invoices' },
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'viewed', label: 'Viewed' },
  { value: 'partial', label: 'Partial' },
  { value: 'paid', label: 'Paid' },
  { value: 'overdue', label: 'Overdue' },
]

const getStatusConfig = (status) => {
  const configs = {
    draft: { bg: 'bg-gray-100', text: 'text-gray-700', icon: FileText, label: 'Draft' },
    sent: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Clock, label: 'Sent' },
    viewed: { bg: 'bg-indigo-100', text: 'text-indigo-700', icon: Eye, label: 'Viewed' },
    partial: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock, label: 'Partial' },
    paid: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle2, label: 'Paid' },
    overdue: { bg: 'bg-red-100', text: 'text-red-700', icon: AlertCircle, label: 'Overdue' },
    void: { bg: 'bg-gray-100', text: 'text-gray-500', icon: X, label: 'Void' },
  }
  return configs[status] || configs.draft
}

export default function Invoices() {
  const { invoices, clients, getClientById, deleteInvoice, sendInvoice, markAsPaid } = useApp()
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [sortBy, setSortBy] = useState('createdAt')
  const [sortOrder, setSortOrder] = useState('desc')
  const [openMenu, setOpenMenu] = useState(null)

  // Filter and sort invoices
  const filteredInvoices = useMemo(() => {
    let result = [...invoices]

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      result = result.filter(inv => {
        const client = getClientById(inv.clientId)
        return (
          inv.invoiceNumber.toLowerCase().includes(query) ||
          client?.name.toLowerCase().includes(query) ||
          inv.total.toString().includes(query)
        )
      })
    }

    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter(inv => inv.status === statusFilter)
    }

    // Sort
    result.sort((a, b) => {
      let aVal, bVal
      switch (sortBy) {
        case 'invoiceNumber':
          aVal = a.invoiceNumber
          bVal = b.invoiceNumber
          break
        case 'total':
          aVal = a.total
          bVal = b.total
          break
        case 'dueDate':
          aVal = new Date(a.dueDate)
          bVal = new Date(b.dueDate)
          break
        default:
          aVal = new Date(a.createdAt)
          bVal = new Date(b.createdAt)
      }
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1
      }
      return aVal < bVal ? 1 : -1
    })

    return result
  }, [invoices, searchQuery, statusFilter, sortBy, sortOrder, getClientById])

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(field)
      setSortOrder('desc')
    }
  }

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this invoice?')) {
      deleteInvoice(id)
    }
    setOpenMenu(null)
  }

  const handleSend = (id) => {
    sendInvoice(id)
    setOpenMenu(null)
  }

  const handleMarkPaid = (id) => {
    markAsPaid(id)
    setOpenMenu(null)
  }

  const getSortIcon = (field) => {
    if (sortBy !== field) return null
    return sortOrder === 'asc' ? '↑' : '↓'
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Invoices</h2>
          <p className="text-gray-600 mt-1">Manage and track all your invoices</p>
        </div>
        <Link to="/invoices/new" className="btn btn-primary">
          <Plus size={20} />
          <span>New Invoice</span>
        </Link>
      </div>

      {/* Filters Bar */}
      <div className="card p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search invoices..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input pl-10"
            />
          </div>

          {/* Status Filter */}
          <div className="flex gap-2 overflow-x-auto pb-2 lg:pb-0">
            {statusFilters.map((filter) => (
              <button
                key={filter.value}
                onClick={() => setStatusFilter(filter.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${statusFilter === filter.value
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
              >
                {filter.label}
                {filter.value !== 'all' && (
                  <span className="ml-2 opacity-75">
                    {invoices.filter(inv => inv.status === filter.value).length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Export Button */}
          <button className="btn btn-secondary">
            <Download size={18} />
            <span className="hidden sm:inline">Export</span>
          </button>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th
                  className="table-header cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('invoiceNumber')}
                >
                  Invoice {getSortIcon('invoiceNumber')}
                </th>
                <th className="table-header">Client</th>
                <th
                  className="table-header cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('dueDate')}
                >
                  Due Date {getSortIcon('dueDate')}
                </th>
                <th
                  className="table-header cursor-pointer hover:bg-gray-100 text-right"
                  onClick={() => handleSort('total')}
                >
                  Amount {getSortIcon('total')}
                </th>
                <th className="table-header">Status</th>
                <th className="table-header text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredInvoices.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <FileText size={48} className="mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">No invoices found</p>
                    <p className="text-sm text-gray-400 mt-1">
                      {searchQuery || statusFilter !== 'all'
                        ? 'Try adjusting your filters'
                        : 'Create your first invoice to get started'
                      }
                    </p>
                    <Link to="/invoices/new" className="btn btn-primary mt-4 inline-flex">
                      <Plus size={18} />
                      Create Invoice
                    </Link>
                  </td>
                </tr>
              ) : (
                filteredInvoices.map((invoice, index) => {
                  const client = getClientById(invoice.clientId)
                  const statusConfig = getStatusConfig(invoice.status)
                  const StatusIcon = statusConfig.icon

                  return (
                    <tr key={invoice.id} className="hover:bg-gray-50 transition-colors">
                      <td className="table-cell">
                        <Link
                          to={`/invoices/${invoice.id}/view`}
                          className="font-medium text-primary-600 hover:text-primary-700"
                        >
                          {invoice.invoiceNumber}
                        </Link>
                        {invoice.subject && (
                          <p className="text-xs text-gray-500 mt-0.5 truncate max-w-[200px]" title={invoice.subject}>
                            {invoice.subject}
                          </p>
                        )}
                      </td>
                      <td className="table-cell">
                        <div>
                          <p className="font-medium text-gray-900">{client?.name || 'Unknown'}</p>
                          <p className="text-xs text-gray-500">{client?.email}</p>
                        </div>
                      </td>
                      <td className="table-cell">
                        <span className={new Date(invoice.dueDate) < new Date() && invoice.status !== 'paid' ? 'text-red-600' : ''}>
                          {new Date(invoice.dueDate).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </span>
                      </td>
                      <td className="table-cell text-right">
                        <span className="font-semibold text-gray-900">
                          ${invoice.total.toLocaleString()}
                        </span>
                        {invoice.partialAmount && invoice.status === 'partial' && (
                          <p className="text-xs text-gray-500">
                            ${invoice.partialAmount.toLocaleString()} paid
                          </p>
                        )}
                      </td>
                      <td className="table-cell">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${statusConfig.bg} ${statusConfig.text}`}>
                          <StatusIcon size={12} />
                          {statusConfig.label}
                        </span>
                      </td>
                      <td className="table-cell text-right">
                        <div className="relative">
                          <button
                            onClick={() => setOpenMenu(openMenu === invoice.id ? null : invoice.id)}
                            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                          >
                            <MoreVertical size={18} className="text-gray-500" />
                          </button>

                          {openMenu === invoice.id && (
                            <>
                              <div
                                className="fixed inset-0 z-10"
                                onClick={() => setOpenMenu(null)}
                              />
                              <div className={`absolute right-0 w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-50 animate-scale-in ${index >= filteredInvoices.length - 2 && filteredInvoices.length > 2
                                ? 'bottom-full mb-1 origin-bottom-right'
                                : 'mt-1 origin-top-right'
                                }`}>
                                <Link
                                  to={`/invoices/${invoice.id}/view`}
                                  className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                >
                                  <Eye size={16} />
                                  View
                                </Link>
                                <Link
                                  to={`/invoices/${invoice.id}/edit`}
                                  className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                >
                                  <Edit size={16} />
                                  Edit
                                </Link>
                                {['draft', 'sent', 'viewed', 'partial'].includes(invoice.status) && (
                                  <button
                                    onClick={() => {
                                      const client = getClientById(invoice.clientId)
                                      const link = `${window.location.origin}/portal/${invoice.id}`
                                      const formattedAmount = invoice.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                      const dueDateStr = new Date(invoice.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                      const clientDisplay = client?.businessName || client?.name || 'Client'
                                      const message = `Hi ${clientDisplay},\n\nNew invoice from ${settings?.businessName || 'us'} is ready.\n\nAmount: $${formattedAmount}\nDue: ${dueDateStr}\n\nView here: ${link}`
                                      const phone = client?.phone ? client.phone.replace(/\D/g, '') : ''
                                      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank')
                                      setOpenMenu(null)
                                    }}
                                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full"
                                  >
                                    <MessageCircle size={16} />
                                    WhatsApp
                                  </button>
                                )}
                                {invoice.status === 'draft' && (
                                  <button
                                    onClick={() => handleSend(invoice.id)}
                                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full"
                                  >
                                    <Send size={16} />
                                    Email
                                  </button>
                                )}
                                {['sent', 'viewed', 'partial'].includes(invoice.status) && (
                                  <button
                                    onClick={() => handleMarkPaid(invoice.id)}
                                    className="flex items-center gap-2 px-4 py-2 text-sm text-green-600 hover:bg-green-50 w-full"
                                  >
                                    <CheckCircle2 size={16} />
                                    Mark as Paid
                                  </button>
                                )}
                                <hr className="my-1" />
                                <button
                                  onClick={() => handleDelete(invoice.id)}
                                  className="flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full"
                                >
                                  <Trash2 size={16} />
                                  Delete
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {filteredInvoices.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Showing {filteredInvoices.length} of {invoices.length} invoices
            </p>
            <div className="flex gap-2">
              <button className="btn btn-secondary text-sm" disabled>
                Previous
              </button>
              <button className="btn btn-secondary text-sm" disabled>
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
